import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Zap, 
  Target,
  Building2, 
  MapPin, 
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Clock,
  ExternalLink,
  Loader2,
  Search,
  RefreshCw,
  Play,
  Users,
  DollarSign,
  Calendar,
  Eye,
  FileText,
  ArrowRight
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import SignalStoryCard from './sales-intelligence/SignalStoryCard';

const CLASSIFICATION_CONFIG = {
  style_opportunity: {
    label: "Style Opportunity",
    color: "bg-green-100 text-green-700 border-green-200",
    icon: CheckCircle,
    description: "Strong signals - auto-created as opportunity"
  },
  high_priority_prospect: {
    label: "High Priority Prospect",
    color: "bg-amber-100 text-amber-700 border-amber-200",
    icon: Target,
    description: "Moderate signals - worth monitoring"
  },
  no_opportunity: {
    label: "No Opportunity",
    color: "bg-gray-100 text-gray-500 border-gray-200",
    icon: AlertCircle,
    description: "Weak or irrelevant signals"
  }
};

const SIGNAL_STRENGTH_COLORS = {
  strong: "bg-green-100 text-green-700",
  moderate: "bg-amber-100 text-amber-700",
  weak: "bg-gray-100 text-gray-600"
};

// Helper to format location which may be string or object
const formatLocation = (location) => {
  if (!location) return 'Unknown location';
  if (typeof location === 'string') return location;
  if (typeof location === 'object') {
    const parts = [location.city, location.province_state, location.country].filter(Boolean);
    return parts.join(', ') || 'Unknown location';
  }
  return 'Unknown location';
};

export function StyleDetectionTab({ token }) {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [unanalyzedSignals, setUnanalyzedSignals] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [filterClassification, setFilterClassification] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, queueRes, unanalyzedRes] = await Promise.all([
        api.get('/style-detection/stats?days=30'),
        api.get('/style-detection/queue?limit=50'),
        api.get('/style-detection/signals/unanalyzed?limit=20')
      ]);
      
      if (statsRes) setStats(statsRes);
      if (queueRes?.records) setQueue(queueRes.records);
      if (unanalyzedRes?.signals) setUnanalyzedSignals(unanalyzedRes.signals);
    } catch (error) {
      console.error('Failed to fetch detection data:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const analyzeSignal = async (signalId) => {
    setAnalyzing(true);
    try {
      const result = await api.post(`/style-detection/analyze/${signalId}?auto_create=true`);
      if (result?.classification) {
        const config = CLASSIFICATION_CONFIG[result.classification] || CLASSIFICATION_CONFIG.no_opportunity;
        toast.success(`Analysis complete: ${config.label}`);
        
        if (result.opportunity_created) {
          toast.success(`Style Opportunity created for ${result.company_name}`);
        }
        
        // Refresh data
        fetchData();
      }
    } catch (error) {
      toast.error(error.message || 'Analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const analyzeAllUnanalyzed = async () => {
    if (unanalyzedSignals.length === 0) {
      toast.info('No unanalyzed signals');
      return;
    }
    
    setAnalyzing(true);
    try {
      const result = await api.post('/style-detection/signals/analyze-unanalyzed?limit=50');
      toast.success(result.message || 'Bulk analysis started');
      
      // Wait and refresh
      setTimeout(fetchData, 3000);
    } catch (error) {
      toast.error(error.message || 'Bulk analysis failed');
    } finally {
      setAnalyzing(false);
    }
  };

  const filteredQueue = queue.filter(record => {
    if (filterClassification !== 'all' && record.classification !== filterClassification) {
      return false;
    }
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        record.company_name?.toLowerCase().includes(search) ||
        record.detected_signal?.toLowerCase().includes(search) ||
        record.industry?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#B89B5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="style-detection-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-green-100 shrink-0">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
              </div>
              <div className="min-w-0">
                <div className="text-xl sm:text-2xl font-semibold text-green-700">{stats?.style_opportunities || 0}</div>
                <div className="text-[10px] sm:text-xs text-muted-foreground truncate">Style Opportunities</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-100">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-amber-100 shrink-0">
                <Target className="h-4 w-4 sm:h-5 sm:w-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <div className="text-xl sm:text-2xl font-semibold text-amber-700">{stats?.high_priority_prospects || 0}</div>
                <div className="text-[10px] sm:text-xs text-muted-foreground truncate">High Priority</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-blue-100 shrink-0">
                <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              </div>
              <div className="min-w-0">
                <div className="text-xl sm:text-2xl font-semibold text-blue-700">{stats?.opportunities_created || 0}</div>
                <div className="text-[10px] sm:text-xs text-muted-foreground truncate">Created</div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-slate-50 to-white border-slate-100">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="p-1.5 sm:p-2 rounded-lg bg-slate-100 shrink-0">
                <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-slate-600" />
              </div>
              <div className="min-w-0">
                <div className="text-xl sm:text-2xl font-semibold text-slate-700">{stats?.total_analyzed || 0}</div>
                <div className="text-[10px] sm:text-xs text-muted-foreground truncate">Analyzed</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Unanalyzed Signals Section */}
      {unanalyzedSignals.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-base">Pending Analysis</CardTitle>
                <Badge variant="outline" className="ml-2 bg-amber-100 text-amber-700">
                  {unanalyzedSignals.length} signals
                </Badge>
              </div>
              <Button 
                onClick={analyzeAllUnanalyzed}
                disabled={analyzing}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="analyze-all-btn"
              >
                {analyzing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Analyze All
              </Button>
            </div>
            <CardDescription>Market signals awaiting Style Opportunity analysis</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {unanalyzedSignals.slice(0, 5).map((signal) => (
                <div 
                  key={signal.id}
                  className="flex items-center justify-between p-3 bg-white rounded-sm border"
                  data-testid={`unanalyzed-signal-${signal.id}`}
                >
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium text-sm">{signal.company_name || 'Unknown Company'}</div>
                      <div className="text-xs text-muted-foreground">
                        {signal.signal_type} • {formatLocation(signal.location) || signal.city || 'Unknown location'}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => analyzeSignal(signal.id)}
                    disabled={analyzing}
                    data-testid={`analyze-signal-${signal.id}`}
                  >
                    {analyzing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                    Analyze
                  </Button>
                </div>
              ))}
              {unanalyzedSignals.length > 5 && (
                <div className="text-center text-sm text-muted-foreground py-2">
                  +{unanalyzedSignals.length - 5} more signals
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detection Queue */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-[#B89B5E]" />
                Detection Queue
              </CardTitle>
              <CardDescription>All analyzed signals and their classifications</CardDescription>
            </div>
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters - stack on mobile */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 text-sm"
                data-testid="detection-search"
              />
            </div>
            <Select value={filterClassification} onValueChange={setFilterClassification}>
              <SelectTrigger className="w-full sm:w-48" data-testid="classification-filter">
                <SelectValue placeholder="All Classifications" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Classifications</SelectItem>
                <SelectItem value="style_opportunity">Style Opportunities</SelectItem>
                <SelectItem value="high_priority_prospect">High Priority</SelectItem>
                <SelectItem value="no_opportunity">No Opportunity</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Queue List */}
          {filteredQueue.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Sparkles className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No detection records found</p>
              <p className="text-sm">Analyze market signals to see results here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredQueue.map((record) => {
                const config = CLASSIFICATION_CONFIG[record.classification] || CLASSIFICATION_CONFIG.no_opportunity;
                const IconComponent = config.icon;
                
                return (
                  <div 
                    key={record.id}
                    className="p-3 sm:p-4 border rounded-sm hover:bg-slate-50 transition-colors cursor-pointer overflow-hidden"
                    onClick={() => setSelectedRecord(record)}
                    data-testid={`detection-record-${record.id}`}
                  >
                    {/* Mobile-first layout */}
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      {/* Left side - main content */}
                      <div className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
                        <div className={`p-1.5 sm:p-2 rounded-lg shrink-0 ${config.color.split(' ').filter(c => c.startsWith('bg-')).join(' ')}`}>
                          <IconComponent className="h-3 w-3 sm:h-4 sm:w-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          {/* Title and badges - wrap on mobile */}
                          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                            <span className="font-medium text-sm sm:text-base truncate max-w-[200px] sm:max-w-none">{record.company_name || 'Unknown'}</span>
                            <Badge variant="outline" className={`${config.color} text-xs shrink-0`}>
                              {config.label}
                            </Badge>
                            {record.opportunity_created && (
                              <Badge className="bg-green-600 text-xs shrink-0">Created</Badge>
                            )}
                          </div>
                          {/* Location - smaller on mobile */}
                          <div className="text-xs sm:text-sm text-muted-foreground mt-1 truncate">
                            <span className="inline-flex items-center gap-1">
                              <MapPin className="h-3 w-3 shrink-0" />
                              {formatLocation(record.location) !== 'Unknown location' 
                                ? formatLocation(record.location) 
                                : formatLocation(record.extracted_location) !== 'Unknown location'
                                  ? formatLocation(record.extracted_location)
                                  : 'Unknown location'}
                            </span>
                            <span className="mx-1 sm:mx-2">•</span>
                            <span>{record.industry_match || record.industry || 'Unknown'}</span>
                          </div>
                          {/* Signal - hidden on mobile to save space */}
                          <div className="hidden sm:block text-sm mt-2">
                            <span className="text-[#B89B5E] font-medium">Signal:</span> {record.detected_signal}
                          </div>
                          {/* Document 2 · Phase 2 — Compact Signal Story (style_opportunity rows only, parity) */}
                          {record.classification === 'style_opportunity' && record.opportunity_id && (
                            <SignalStoryCard
                              opportunityId={record.opportunity_id}
                              opportunity={record}
                              variant="compact"
                            />
                          )}
                        </div>
                      </div>
                      
                      {/* Right side - stats and actions */}
                      <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-2 sm:gap-1 shrink-0">
                        <div className="flex items-center gap-1 sm:gap-2">
                          {record.signal_strength && (
                            <Badge variant="outline" className={`${SIGNAL_STRENGTH_COLORS[record.signal_strength]} text-xs`}>
                              {record.signal_strength}
                            </Badge>
                          )}
                          {record.confidence_score > 0 && (
                            <span className="text-xs sm:text-sm font-medium text-muted-foreground whitespace-nowrap">
                              {record.confidence_score}%
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                          <div className="text-xs text-muted-foreground">
                            {new Date(record.created_at).toLocaleDateString()}
                          </div>
                          {record.opportunity_id && (
                            <Link 
                              to={`/opportunity/${record.opportunity_id}`}
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              View <ArrowRight className="h-3 w-3" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Signal text - shown only on mobile as expandable */}
                    <div className="sm:hidden text-xs text-muted-foreground mt-2 line-clamp-2">
                      <span className="text-[#B89B5E] font-medium">Signal:</span> {record.detected_signal}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog
        open={!!selectedRecord}
        onOpenChange={(open) => {
          // Prevent backdrop/escape from closing the modal while a long-running
          // enrichment is in flight — the user would otherwise lose all context.
          if (enriching) return;
          if (!open) setSelectedRecord(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[92vh] sm:max-h-[85vh] overflow-y-auto">
          {selectedRecord && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-[#B89B5E]" />
                  {selectedRecord.company_name}
                </DialogTitle>
                <DialogDescription>
                  Style Opportunity Detection Analysis
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Classification */}
                <div className="flex items-center gap-3">
                  <Badge variant="outline" className={CLASSIFICATION_CONFIG[selectedRecord.classification]?.color || ''}>
                    {CLASSIFICATION_CONFIG[selectedRecord.classification]?.label || selectedRecord.classification}
                  </Badge>
                  {selectedRecord.opportunity_created && (
                    <Badge className="bg-green-600">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Opportunity Created
                    </Badge>
                  )}
                </div>

                {/* Key Info Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Industry</div>
                    <div className="font-medium">{selectedRecord.industry_match || selectedRecord.industry || 'Unknown'}</div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Location</div>
                    <div className="font-medium">
                      {formatLocation(selectedRecord.location) !== 'Unknown location' 
                        ? formatLocation(selectedRecord.location) 
                        : formatLocation(selectedRecord.extracted_location) !== 'Unknown location'
                          ? formatLocation(selectedRecord.extracted_location)
                          : 'Unknown location'}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Signal Strength</div>
                    <Badge variant="outline" className={SIGNAL_STRENGTH_COLORS[selectedRecord.signal_strength]}>
                      {selectedRecord.signal_strength || 'Unknown'}
                    </Badge>
                  </div>
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Confidence</div>
                    <div className="font-medium">{selectedRecord.confidence_score || 0}%</div>
                  </div>
                </div>

                {/* Extracted Companies (if available and different from company_name) */}
                {selectedRecord.extracted_companies && selectedRecord.extracted_companies.length > 0 && (
                  <div className="p-3 bg-blue-50 rounded-sm border border-blue-100">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Identified Companies</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedRecord.extracted_companies.map((company, idx) => (
                        <Badge key={idx} variant="outline" className="bg-white">
                          {company}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* TIER 1: SERP Resolution Data */}
                {(selectedRecord.resolved_company_name || selectedRecord.source_url || selectedRecord.named_decision_makers?.length > 0 || selectedRecord.serp_resolution_status) && (
                  <div className="p-3 bg-purple-50 rounded-sm border border-purple-100">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                      <Target className="h-3 w-3" />
                      Tier 1: SERP Discovery
                      {selectedRecord.serp_resolution_status === 'completed' && (
                        <Badge variant="outline" className="text-xs bg-purple-100">Resolved</Badge>
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      {selectedRecord.resolved_company_name && (
                        <div><span className="font-medium">Resolved Company:</span> {selectedRecord.resolved_company_name}</div>
                      )}
                      {(selectedRecord.likely_company_domain || selectedRecord.company_domain) && (
                        <div><span className="font-medium">Domain:</span> {selectedRecord.likely_company_domain || selectedRecord.company_domain}</div>
                      )}
                      {selectedRecord.source_url && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Source:</span> 
                          <a href={selectedRecord.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-[300px]">
                            {selectedRecord.source_title || selectedRecord.source_url}
                          </a>
                          <ExternalLink className="h-3 w-3 text-blue-500" />
                        </div>
                      )}
                      {selectedRecord.serp_resolution_confidence > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Confidence:</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                            <div 
                              className="bg-purple-500 h-2 rounded-full" 
                              style={{width: `${selectedRecord.serp_resolution_confidence}%`}}
                            />
                          </div>
                          <span className="text-xs">{selectedRecord.serp_resolution_confidence}%</span>
                        </div>
                      )}
                      {selectedRecord.named_decision_makers?.length > 0 && (
                        <div>
                          <span className="font-medium">Named Decision Makers:</span>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {selectedRecord.named_decision_makers.map((dm, i) => (
                              <Badge key={i} variant="outline" className="bg-white text-purple-700">
                                <Users className="h-3 w-3 mr-1" />
                                {dm.name} {dm.title && `- ${dm.title}`}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* TIER 2: Google Business Data */}
                {(selectedRecord.business_phone || selectedRecord.business_website || selectedRecord.formatted_address || selectedRecord.google_resolution_status) && (
                  <div className="p-3 bg-blue-50 rounded-sm border border-blue-100">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                      <Building2 className="h-3 w-3" />
                      Tier 2: Google Business Data
                      {selectedRecord.google_resolution_status === 'completed' && (
                        <Badge variant="outline" className="text-xs bg-blue-100">Resolved</Badge>
                      )}
                    </div>
                    <div className="space-y-2 text-sm">
                      {selectedRecord.formatted_address && (
                        <div><span className="font-medium">Address:</span> {selectedRecord.formatted_address}</div>
                      )}
                      {selectedRecord.business_phone && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Phone:</span> 
                          <a href={`tel:${selectedRecord.business_phone}`} className="text-blue-600 hover:underline">
                            {selectedRecord.business_phone}
                          </a>
                          <Badge variant="outline" className="text-xs">{selectedRecord.phone_source || 'Google'}</Badge>
                        </div>
                      )}
                      {selectedRecord.business_website && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Website:</span> 
                          <a href={selectedRecord.business_website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-[300px]">
                            {selectedRecord.business_website}
                          </a>
                          <ExternalLink className="h-3 w-3 text-blue-500" />
                        </div>
                      )}
                      {selectedRecord.latitude && selectedRecord.longitude && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Coordinates:</span> 
                          <span className="text-gray-600">{selectedRecord.latitude.toFixed(4)}, {selectedRecord.longitude.toFixed(4)}</span>
                          <MapPin className="h-3 w-3 text-gray-500" />
                        </div>
                      )}
                      {selectedRecord.google_resolution_confidence > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Confidence:</span>
                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-[100px]">
                            <div 
                              className="bg-blue-500 h-2 rounded-full" 
                              style={{width: `${selectedRecord.google_resolution_confidence}%`}}
                            />
                          </div>
                          <span className="text-xs">{selectedRecord.google_resolution_confidence}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* TIER 3: Apollo Enrichment Status */}
                {selectedRecord.apollo_enrichment_timestamp && (
                  <div className="p-3 bg-green-50 rounded-sm border border-green-100">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-2">
                      <Users className="h-3 w-3" />
                      Tier 3: Apollo Contact Enrichment
                      <Badge variant="outline" className="text-xs bg-green-100">Processed</Badge>
                    </div>
                    <div className="text-sm text-gray-600">
                      Contacts enriched on {new Date(selectedRecord.apollo_enrichment_timestamp).toLocaleDateString()}
                    </div>
                  </div>
                )}

                {/* Resolve & Enrich Button - show if not fully resolved */}
                {(!selectedRecord.serp_resolution_status || selectedRecord.serp_resolution_status !== 'completed') && (
                  <Button 
                    variant="outline" 
                    className="w-full border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-70"
                    data-testid="run-3tier-enrichment-btn"
                    disabled={enriching}
                    onClick={async () => {
                      setEnriching(true);
                      try {
                        toast.info('Running 3-Tier Lead Intelligence — this can take 20–30 seconds...');
                        // IMPORTANT: the backend full-enrich endpoint looks the signal
                        // up in `db.market_signals` keyed by `signal_id`. The Style
                        // Detection record carries TWO ids: its own record `id` (in
                        // the detection queue) and `signal_id` (the underlying market
                        // signal). Passing `selectedRecord.id` makes the backend 404
                        // silently and every tier returns ✗ — so always pass signal_id.
                        const enrichTargetId = selectedRecord.signal_id || selectedRecord.id;
                        const result = await api.post(`/lead-intelligence/signals/${enrichTargetId}/full-enrich`);
                        if (result.status === 'completed') {
                          const summary = result.summary || {};
                          toast.success(
                            `Enrichment complete — Company: ${summary.company_resolved ? '✓' : '✗'}, ` +
                            `Phone: ${summary.phone_found ? '✓' : '✗'}, ` +
                            `DMs: ${summary.decision_makers_found || 0}`
                          );

                          // Auto-prospect creation result (Option C — quality-gated).
                          // The backend evaluates SERP confidence, DM count, and
                          // domain quality. On pass it spawns Account + Contacts +
                          // Opportunity in the pipeline. On skip it returns a
                          // human-readable reason so the user knows why.
                          const ap = result.auto_prospect || {};
                          if (ap.created) {
                            toast.success(
                              `Saved to Pipeline — ${ap.contacts_created} contact${ap.contacts_created === 1 ? '' : 's'} created`,
                              { duration: 6000 }
                            );
                          } else if (ap.already_exists) {
                            toast.info('Already in your pipeline');
                          } else if (ap.reason) {
                            toast.info(`Not auto-saved: ${ap.reason}. Use "Save as Prospect" to override.`, { duration: 6000 });
                          }
                        } else {
                          toast.info(result.message || 'Enrichment finished with no new data');
                        }
                        // Always refresh the list + this record so the modal
                        // reflects backend state even on partial / no-op runs.
                        fetchData();
                        try {
                          const refreshed = await api.get(`/style-detection/queue/${selectedRecord.id}`);
                          if (refreshed) setSelectedRecord(refreshed);
                        } catch (e) {
                          console.warn('Could not refresh enriched record', e);
                        }
                      } catch (error) {
                        toast.error('Failed to run lead intelligence');
                      } finally {
                        setEnriching(false);
                      }
                    }}
                  >
                    {enriching ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Enriching… please wait
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Run 3-Tier Enrichment (SERP → Google → Apollo)
                      </>
                    )}
                  </Button>
                )}

                {/* Manual "Save as Prospect" override — visible once enrichment
                    has populated at least a resolved company. Lets the user push
                    sub-threshold signals into the pipeline anyway when they
                    judge them worthwhile. Shows a "Saved" badge when the
                    backend has already created the prospect. */}
                {(selectedRecord.resolved_company_name || selectedRecord.serp_resolution_status === 'completed') && (
                  selectedRecord.auto_prospect_id ? (
                    <div
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm"
                      data-testid="prospect-saved-badge"
                    >
                      <CheckCircle className="h-4 w-4" />
                      Saved to Pipeline
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full border-[#B89B5E] text-[#1C1C1C] hover:bg-[#FAF8F5]"
                      data-testid="save-as-prospect-btn"
                      disabled={enriching}
                      onClick={async () => {
                        const enrichTargetId = selectedRecord.signal_id || selectedRecord.id;
                        try {
                          toast.info('Saving to pipeline…');
                          const result = await api.post(
                            `/lead-intelligence/signals/${enrichTargetId}/save-as-prospect`
                          );
                          if (result.created) {
                            toast.success(
                              `Saved to Pipeline — ${result.contacts_created} contact${result.contacts_created === 1 ? '' : 's'} created`,
                              { duration: 6000 }
                            );
                          } else if (result.already_exists) {
                            toast.info('Already in your pipeline');
                          } else {
                            toast.error(result.reason || 'Could not save');
                          }
                          // Refresh the modal so the "Saved" badge appears
                          try {
                            const refreshed = await api.get(`/style-detection/queue/${selectedRecord.id}`);
                            if (refreshed) setSelectedRecord(refreshed);
                          } catch (e) {
                            console.warn('refresh after save failed', e);
                          }
                          fetchData();
                        } catch (e) {
                          toast.error('Failed to save as prospect');
                        }
                      }}
                    >
                      Save as Prospect (override)
                    </Button>
                  )
                )}

                {/* Detected Signal */}
                <div className="p-3 bg-[#FAF9F7] rounded-sm border">
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Detected Signal</div>
                  <div className="font-medium">{selectedRecord.detected_signal}</div>
                </div>

                {/* Operational Pressure */}
                {selectedRecord.operational_pressure && (
                  <div className="p-3 bg-amber-50 rounded-sm border border-amber-100">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Operational Pressure</div>
                    <div className="font-medium text-amber-800">{selectedRecord.operational_pressure}</div>
                  </div>
                )}

                {/* Opportunity Narrative */}
                {selectedRecord.opportunity_narrative && (
                  <div className="space-y-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Opportunity Narrative</div>
                    <div className="text-sm leading-relaxed">{selectedRecord.opportunity_narrative}</div>
                  </div>
                )}

                {/* Suggested Decision Makers */}
                {selectedRecord.suggested_decision_makers?.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider">Suggested Decision Makers</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedRecord.suggested_decision_makers.map((dm, i) => (
                        <Badge key={i} variant="outline" className="bg-blue-50">
                          <Users className="h-3 w-3 mr-1" />
                          {dm}
                        </Badge>
                      ))}
                    </div>
                    {selectedRecord.primary_outreach_target && (
                      <div className="text-sm">
                        <span className="font-medium">Primary Target:</span> {selectedRecord.primary_outreach_target}
                      </div>
                    )}
                  </div>
                )}

                {/* Value & Timeline */}
                <div className="grid grid-cols-2 gap-4">
                  {selectedRecord.estimated_value_range && (
                    <div className="p-3 bg-green-50 rounded-sm border border-green-100">
                      <div className="flex items-center gap-2 text-green-700">
                        <DollarSign className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wider">Est. Value</span>
                      </div>
                      <div className="font-semibold text-green-800 mt-1">
                        ${(selectedRecord.estimated_value_range.min || 0).toLocaleString()} - ${(selectedRecord.estimated_value_range.max || 0).toLocaleString()}
                      </div>
                    </div>
                  )}
                  {selectedRecord.estimated_close_window && (
                    <div className="p-3 bg-blue-50 rounded-sm border border-blue-100">
                      <div className="flex items-center gap-2 text-blue-700">
                        <Calendar className="h-4 w-4" />
                        <span className="text-xs uppercase tracking-wider">Close Window</span>
                      </div>
                      <div className="font-semibold text-blue-800 mt-1">
                        {selectedRecord.estimated_close_window}
                      </div>
                    </div>
                  )}
                </div>

                {/* Reasoning */}
                {selectedRecord.reasoning && (
                  <div className="p-3 bg-slate-50 rounded-sm border">
                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">AI Reasoning</div>
                    <div className="text-sm text-slate-700">{selectedRecord.reasoning}</div>
                  </div>
                )}

                {/* Link to Opportunity */}
                {selectedRecord.opportunity_id && (
                  <div className="pt-4 border-t">
                    <Link to={`/opportunity/${selectedRecord.opportunity_id}`}>
                      <Button className="w-full bg-[#1A1A1A] hover:bg-[#2A2A2A]">
                        <Eye className="h-4 w-4 mr-2" />
                        View Created Opportunity
                      </Button>
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StyleDetectionTab;
